import baseApi from './baseApi';

interface MetaConnection {
  id: number;
  metaBusinessId: string;
  metaBusinessName: string;
  isActive: boolean;
  isExpired: boolean;
  isExpiringSoon: boolean;
  adAccountsCount?: number;
  pendingAdAccountsCount?: number;
  assignedAdAccountsCount?: number;
  pixelConfiguredCount?: number;
  eventsConfiguredCount?: number;
  subscribedPagesCount?: number;
  lastSyncAt: string | null;
  expiresAt: string | null;
  createdAt?: string;
  updatedAt?: string;
  linkType?: 'direct';
}

interface ConnectionsForBusinessUnitResponse {
  connections: MetaConnection[];
}

interface MetaPixel {
  id: string;
  name: string;
  creation_time?: string;
  last_fired_time?: string;
  code?: string;
}

interface AvailablePixelsResponse {
  pixels: MetaPixel[];
  businessId: string;
}

interface ConfigurePixelResponse {
  success: boolean;
  metaAdAccount: {
    id: number;
    metaAdAccountId: string;
    name: string;
    metaPixelId: string;
  };
  message: string;
}

interface UpdateNameEventsRequest {
  nameEvents: {
    BOFU?: string;
  };
}

interface UpdateNameEventsResponse {
  success: boolean;
  metaAdAccount: {
    id: number;
    metaAdAccountId: string;
    name: string;
    nameEvents: {
      BOFU?: string;
    };
  };
  message: string;
}

interface MetaAd {
  id: string;
  name: string;
  status: string;
  adset_id: string;
  campaign_id: string;
  creative?: object;
  tracking_specs?: object;
  conversion_specs?: object;
  lastSyncAt?: string;
}

interface MetaAdSet {
  id: string;
  name: string;
  status: string;
  campaign_id: string;
  daily_budget?: string;
  lifetime_budget?: string;
  start_time?: string;
  end_time?: string;
  targeting?: object;
  optimization_goal?: string;
  billing_event?: string;
  bid_amount?: string;
  lastSyncAt?: string;
  ads: MetaAd[];
}

interface MetaCampaign {
  id: string;
  name: string;
  status: string;
  objective?: string;
  daily_budget?: string;
  lifetime_budget?: string;
  start_time?: string;
  stop_time?: string;
  created_time?: string;
  updated_time?: string;
  buying_type?: string;
  bid_strategy?: string;
  lastSyncAt?: string;
  adSets: MetaAdSet[];
}

interface MetaAdAccount {
  id: number;
  metaAdAccountId: string;
  name: string;
  accountStatus?: number;
  currency?: string;
  lastSyncAt?: string;
  metaPixelId?: string | null;
  nameEvents?: { BOFU?: string | null } | null;
  campaigns?: MetaCampaign[];
  metaConnection?: MetaConnection;
}

interface MetaPageRow {
  metaPageId: string;
  pageName: string;
  isSubscribedToLeadgen: boolean;
  businessUnitId: number | null;
  isStored: boolean;
}

interface MetaPagesResponse {
  pages: MetaPageRow[];
}

interface SubscribeMetaPageRequest {
  metaConnectionId: number;
  metaPageId: string;
  businessUnitId: number;
}

interface SubscribeMetaPageResponse {
  success: boolean;
  metaPage: {
    id: number;
    metaPageId: string;
    pageName: string;
    businessUnitId: number | null;
    isSubscribedToLeadgen: boolean;
  };
}

interface UnsubscribeMetaPageResponse {
  success: boolean;
}

interface BrowserDiagnostics {
  fbLoginResponse: Record<string, unknown>;
  postMessages: Array<{
    timestamp: number;
    origin: string;
    data: unknown;
    parsed?: unknown;
  }>;
}

interface SubmitEmbeddedSignupCodeRequest {
  code: string;
  businessUnitId: number;
  browserDiagnostics?: BrowserDiagnostics;
}

interface SubmitEmbeddedSignupCodeResponse {
  success: boolean;
  connectionId: number;
  metaBusinessId: string;
  metaBusinessName: string;
  adAccountsCount: number;
  pagesCount: number;
}

const relifMetaApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    submitEmbeddedSignupCode: build.mutation<SubmitEmbeddedSignupCodeResponse, SubmitEmbeddedSignupCodeRequest>({
      query: (body) => ({
        url: '/relif-meta/embedded-signup',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _err, { businessUnitId }) => [{ type: 'MetaConnection', id: businessUnitId }],
      transformResponse: (response: SubmitEmbeddedSignupCodeResponse) => response,
    }),
    getMetaConnectionsForBusinessUnit: build.query<ConnectionsForBusinessUnitResponse, number>({
      query: (businessUnitId) => ({
        url: `/relif-meta/connections-for-business-unit/${businessUnitId}`,
        method: 'GET',
      }),
      providesTags: (_result, _err, businessUnitId) => [{ type: 'MetaConnection', id: businessUnitId }],
      transformResponse: (response: ConnectionsForBusinessUnitResponse) => response,
    }),
    getAvailablePixels: build.query<AvailablePixelsResponse, string>({
      query: (businessId: string) => ({
        url: `/meta-ad-accounts/get-available-pixels/${businessId}`,
        method: 'GET',
      }),
      transformResponse: (response: AvailablePixelsResponse) => response,
    }),
    configurePixel: build.mutation<ConfigurePixelResponse, { metaAdAccountId: string; metaPixelId: string }>({
      query: ({ metaAdAccountId, metaPixelId }) => ({
        url: `/meta-ad-accounts/${metaAdAccountId}/pixel`,
        method: 'POST',
        body: { metaPixelId },
      }),
      transformResponse: (response: ConfigurePixelResponse) => response,
    }),
    updateNameEvents: build.mutation<
      UpdateNameEventsResponse,
      { metaAdAccountId: string; nameEvents: UpdateNameEventsRequest['nameEvents'] }
    >({
      query: ({ metaAdAccountId, nameEvents }) => ({
        url: `/meta-ad-accounts/${metaAdAccountId}/name-events`,
        method: 'PATCH',
        body: { nameEvents },
      }),
      transformResponse: (response: UpdateNameEventsResponse) => response,
    }),
    getAdAccountsByBusinessUnit: build.query<
      { adAccounts: MetaAdAccount[] },
      { businessUnitId: number; metaConnectionId?: number } | number
    >({
      query: (arg) => {
        const businessUnitId = typeof arg === 'number' ? arg : arg.businessUnitId;
        const metaConnectionId = typeof arg === 'number' ? undefined : arg.metaConnectionId;
        return {
          url: `/meta-ad-accounts/business-unit/${businessUnitId}`,
          method: 'GET',
          params: metaConnectionId ? { metaConnectionId } : undefined,
        };
      },
      transformResponse: (response: { adAccounts: MetaAdAccount[] }) => response,
    }),
    getMetaPages: build.query<MetaPagesResponse, { connectionId: number; businessUnitId?: number } | number>({
      query: (arg) => {
        const connectionId = typeof arg === 'number' ? arg : arg.connectionId;
        const businessUnitId = typeof arg === 'number' ? undefined : arg.businessUnitId;
        return {
          url: `/meta-lead-ads/pages/${connectionId}`,
          method: 'GET',
          params: businessUnitId ? { businessUnitId } : undefined,
        };
      },
      transformResponse: (response: MetaPagesResponse) => response,
    }),
    subscribeMetaPage: build.mutation<SubscribeMetaPageResponse, SubscribeMetaPageRequest>({
      query: (body) => ({
        url: '/meta-lead-ads/subscribe',
        method: 'POST',
        body,
      }),
      transformResponse: (response: SubscribeMetaPageResponse) => response,
    }),
    unsubscribeMetaPage: build.mutation<UnsubscribeMetaPageResponse, string>({
      query: (metaPageId) => ({
        url: `/meta-lead-ads/unsubscribe/${metaPageId}`,
        method: 'POST',
      }),
      transformResponse: (response: UnsubscribeMetaPageResponse) => response,
    }),
  }),
  overrideExisting: false,
});

export const {
  useSubmitEmbeddedSignupCodeMutation,
  useGetMetaConnectionsForBusinessUnitQuery,
  useLazyGetAvailablePixelsQuery,
  useConfigurePixelMutation,
  useUpdateNameEventsMutation,
  useGetAdAccountsByBusinessUnitQuery,
  useGetMetaPagesQuery,
  useSubscribeMetaPageMutation,
  useUnsubscribeMetaPageMutation,
} = relifMetaApi;

export type {
  AvailablePixelsResponse,
  ConfigurePixelResponse,
  ConnectionsForBusinessUnitResponse,
  MetaAd,
  MetaAdAccount,
  MetaAdSet,
  MetaCampaign,
  MetaConnection,
  MetaPageRow,
  MetaPagesResponse,
  MetaPixel,
  SubmitEmbeddedSignupCodeRequest,
  SubmitEmbeddedSignupCodeResponse,
  UpdateNameEventsRequest,
  UpdateNameEventsResponse,
};
